// ============================================================================
// ADR-0024 — the engagement's own directors list (Corporate Facts `directors`)
// ============================================================================
// The director master is not engagement-linked, so an old year must not read
// it: a rename, a corrected date or a deleted record would change a signed
// Board's Report. The engagement keeps its own copy — seeded from the master,
// changed only by the auditor (arrange, remove, Sync from master) — and the
// ARRAY ORDER is the order every screen and document lists the directors in.
// Mirrors prima-api FactsDirectors.

import type { CorporateFactsDirector, MasterDirectorEntry } from 'api/types/fsa/corporateFacts.types';

const iso = (s: string | null | undefined): string | undefined => (s ? s.slice(0, 10) : undefined);

/** A master director as a list entry (details copied now, never re-read). */
export function fromMaster(m: MasterDirectorEntry): CorporateFactsDirector {
  const e: CorporateFactsDirector = { director_id: m.directorId.toLowerCase(), name: m.name };
  if (m.din) e.din = m.din;
  if (m.designation) e.designation = m.designation;
  if (iso(m.appointed)) e.appointed = iso(m.appointed);
  if (iso(m.ceased)) e.ceased = iso(m.ceased);
  return e;
}

const sameId = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

/** In office on `date` (yyyy-MM-dd); a missing appointment / cessation date is open. */
export function inOffice(d: CorporateFactsDirector, date: string): boolean {
  if (!date) return false;
  return (!d.appointed || d.appointed <= date) && (!d.ceased || d.ceased >= date);
}

/** In office at any point between `from` and `to` (yyyy-MM-dd). */
export function overlaps(d: CorporateFactsDirector, from: string, to: string): boolean {
  return (!d.appointed || d.appointed <= to) && (!d.ceased || d.ceased >= from);
}

export interface RosterDrift {
  /** In the list, but the master now has different details. */
  changed: CorporateFactsDirector[];
  /** In the master for the year, not in the list. */
  added: MasterDirectorEntry[];
  /** In the list, not in the master for the year (deleted, or dates moved out of the year). */
  missing: CorporateFactsDirector[];
}

const detailsDiffer = (d: CorporateFactsDirector, m: CorporateFactsDirector) =>
  (d.name ?? '') !== (m.name ?? '') || (d.din ?? '') !== (m.din ?? '') || (d.designation ?? '') !== (m.designation ?? '')
  || (d.appointed ?? '') !== (m.appointed ?? '') || (d.ceased ?? '') !== (m.ceased ?? '');

/** How the list differs from the master for the year — shown as a notice, never applied on its own. */
export function rosterDrift(roster: CorporateFactsDirector[], master: MasterDirectorEntry[]): RosterDrift {
  const changed: CorporateFactsDirector[] = [];
  const missing: CorporateFactsDirector[] = [];
  for (const d of roster) {
    const m = master.find(x => sameId(x.directorId, d.director_id));
    if (!m) missing.push(d);
    else if (detailsDiffer(d, fromMaster(m))) changed.push(d);
  }
  const added = master.filter(m => !roster.some(d => sameId(d.director_id, m.directorId)));
  return { changed, added, missing };
}

export const hasDrift = (d: RosterDrift) => d.changed.length + d.added.length + d.missing.length > 0;

/**
 * Sync from master: each listed director takes the master's current details
 * (the order is kept), directors new to the master for the year go at the
 * end in the master's default order. Directors no longer in the master stay —
 * removing one is the auditor's decision.
 */
export function syncRoster(roster: CorporateFactsDirector[], master: MasterDirectorEntry[]): CorporateFactsDirector[] {
  const updated = roster.map(d => {
    const m = master.find(x => sameId(x.directorId, d.director_id));
    return m ? fromMaster(m) : d;
  });
  const added = master.filter(m => !roster.some(d => sameId(d.director_id, m.directorId))).map(fromMaster);
  return [...updated, ...added];
}

/** The list with the entry at `index` moved one place up (-1) or down (+1). */
export function moveEntry<T>(list: T[], index: number, delta: -1 | 1): T[] {
  const to = index + delta;
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  [next[index], next[to]] = [next[to], next[index]];
  return next;
}
